import usePageFilterOptions from "../hooks/usePageFilterOptions";
// src/components/CompanyFiltersDrawer.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
} from "@mui/material";

import { FilterSearch } from "iconsax-react";

import ReusableRightDrawer from "./ReusableRightDrawer";
import PopupMultiSelectField, { popupFilterInputSx } from "./PopupMultiSelectField";
import LocationMultiSelectField from "./LocationMultiSelectField";
import {
  groupLocationOption,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";

const uniq = (arr) =>
  Array.from(new Set([].concat(arr || []).filter(Boolean).map((s) => String(s).trim()).filter(Boolean)));

export const DEFAULT_FILTERS = {
  ai: "",
  companyNames: [],
  techKeywords: [],
  locations: [],
  owners: [],
  statuses: [],
  activities: [],
  sources: [],
};

export default function CompanyFiltersDrawer({
  open,
  onClose,

  // ✅ rows used to build dropdown options
  rows = [],

  // controlled
  value,
  onChange,

  // callbacks
  onApply,
  onClear,

  // ✅ NEW: open Advanced Search view
  // parent should open CompanyAdvancedSearchView when called
  onAdvancedSearch,

  endpoint = "/api/leads",
  ownerLabel = "Owner",
}) {
  const [local, setLocal] = useState(value || DEFAULT_FILTERS);

  useEffect(() => {
    setLocal(value || DEFAULT_FILTERS);
  }, [value]);

  const setFilters = (next) => {
    setLocal(next);
  };

  const directory = usePageFilterOptions(open, endpoint, local, setLocal);
  const mergedOptions = directory.options;

  // Styles (match screenshot)
  const PURPLE = "#9C4FFF";
  const PURPLE_DARK = "#6d28d9";
  const LABEL_BLUE = "#3451F4";

  const sectionLabelSx = {
    fontSize: 14,
    fontWeight: 700,
    color: LABEL_BLUE,
    mb: 0.9,
    fontFamily: "Be Vietnam Pro",
  };

  const inputSx = popupFilterInputSx(PURPLE);

  const dividerSx = { my: 1.6, borderColor: "#eef2f7" };

  const setField = (key, next) => setFilters({ ...local, [key]: next });

  const clearAllLocal = () => {
    setFilters(DEFAULT_FILTERS); // ✅ resets UI + parent
    onClear?.();
  };

  const applyNow = () => {
    onChange?.(local);
    onApply?.(local); // ✅ apply current selections
    onClose?.();
  };

  // ✅ THIS is the important change:
  // Advanced Search opens the advanced view instead of calling onApply
  const openAdvancedSearch = () => {
    onAdvancedSearch?.(local); // ✅ parent shows CompanyAdvancedSearchView
    onClose?.();
  };

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "999px",
          border: `1px solid ${LABEL_BLUE}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
          flex: "0 0 auto",
        }}
      >
        <FilterSearch size={16} color={LABEL_BLUE} variant="Outline" />
      </Box>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: LABEL_BLUE,
          fontFamily: "Be Vietnam Pro",
        }}
      >
        Filter by
      </Typography>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="company-filters-drawer"
      width={546}
      headerLeft={headerLeft}
      closeVariant="contained"
      closeColor={PURPLE}
      paperSx={{ overflow: "hidden" }}
      footer={null}
    >
      {/* Scrollable content area */}
      <Box sx={{ px: 0, pb: 11 }}>
        {directory.loading && <Typography role="status">Loading all filter values...</Typography>}
        {directory.error && <Alert severity="error">{directory.error}</Alert>}
        {/* AI Agent */}
        <Typography sx={sectionLabelSx}>AI Agent:</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Type your request"
          value={local.ai || ""}
          onChange={(e) => setField("ai", e.target.value)}
          sx={inputSx}
        />

        <Divider sx={dividerSx} />

        {/* Company Name */}
        <Typography sx={sectionLabelSx}>Company Name:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.companyNames}
          {...directory.fieldProps("companyNames")}
          value={local.companyNames || []}
          onChange={(next) => setField("companyNames", uniq(next))}
          placeholder="Type company name"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        {/* Technology Stack */}
        <Typography sx={sectionLabelSx}>Technology Stack:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.skills}
          {...directory.fieldProps("skills")}
          value={local.techKeywords || []}
          onChange={(next) => setField("techKeywords", uniq(next))}
          placeholder="Search existing skills"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        {/* Locations */}
        <Typography sx={sectionLabelSx}>Locations:</Typography>
        <LocationMultiSelectField
          freeSolo={false}
          searchExternal={false}
          options={mergedOptions.locations}
          {...directory.fieldProps("locations")}
          value={local.locations || []}
          onChange={(next) => setField("locations", normalizeLocationFilterValues(next))}
          placeholder="Select location"
          inputSx={inputSx}
          autocompleteProps={{ ...directory.fieldProps("locations").autocompleteProps, groupBy: groupLocationOption }}
        />

        <Divider sx={dividerSx} />

        {/* Owner */}
        <Typography sx={sectionLabelSx}>{ownerLabel}:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.owners}
          {...directory.fieldProps("owners")}
          value={local.owners || []}
          onChange={(next) => setField("owners", uniq(next))}
          placeholder={`Select one or more ${ownerLabel.toLowerCase()}s`}
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        {/* Status */}
        <Typography sx={sectionLabelSx}>Status:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.statuses}
          {...directory.fieldProps("statuses")}
          value={local.statuses || []}
          onChange={(next) => setField("statuses", uniq(next))}
          placeholder="Select one or more Status"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        {/* Activity */}
        <Typography sx={sectionLabelSx}>Activity:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.activities}
          {...directory.fieldProps("activities")}
          value={local.activities || []}
          onChange={(next) => setField("activities", uniq(next))}
          placeholder="Select one or more activities"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        {/* Source */}
        <Typography sx={sectionLabelSx}>Source:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.sources}
          {...directory.fieldProps("sources")}
          value={local.sources || []}
          onChange={(next) => setField("sources", uniq(next))}
          placeholder="Select one or more Sources"
          inputSx={inputSx}
        />
      </Box>

      {/* ✅ Sticky bottom buttons */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 5,
          backgroundColor: "#fff",
          borderTop: "1px solid #eef2f7",
          px: 2,
          py: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          {typeof onAdvancedSearch === "function" ? (
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12,
                fontFamily: "Be Vietnam Pro",
                backgroundColor: PURPLE,
                boxShadow: "none",
                px: 2.2,
                "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
              }}
              onClick={openAdvancedSearch}
            >
              Advance Search
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              borderColor: PURPLE,
              color: PURPLE,
              px: 2.2,
              "&:hover": {
                borderColor: PURPLE_DARK,
                color: PURPLE_DARK,
                backgroundColor: "transparent",
              },
            }}
            onClick={clearAllLocal}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              boxShadow: "none",
              px: 2.2,
              "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
            }}
            onClick={applyNow}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </ReusableRightDrawer>
  );
}
